// ---------------------------------------------------------------------------
// Role-driven My Performance view configuration.
//
// Both SALESREP and RBD render the same tabbed view (hero card + pill tabs +
// per-tab DashboardRenderer). This module is the single source of truth for
// each role's hero props key, tab labels, per-tab layouts / switch options,
// and which widgets are unavailable (no data yet). SALESREP config reproduces
// the previous hard-coded SalesRepWidgetTabs behaviour exactly; LEADERSHIP has
// no config here and keeps the legacy single-grid path in MyPerformance.js.
// ---------------------------------------------------------------------------

import {
  SALESREP_MY_PERF_LAYOUT_SCOPES,
  SALESREP_MY_PERF_PERFORMANCE_LAYOUT,
  SALESREP_MY_PERF_HCP_LAYOUT,
  SALESREP_MY_PERF_COMPETITOR_LAYOUT,
  SALESREP_MY_PERF_SWITCH_OPTIONS,
  SALESREP_MY_PERF_HCP_EXTRA_SWITCH_OPTIONS,
  SALESREP_MY_PERF_PERFORMANCE_EXTRA_SWITCH_OPTIONS,
  RBD_MY_PERF_LAYOUT_SCOPES,
  RBD_MY_PERF_PERFORMANCE_LAYOUT,
  RBD_MY_PERF_HCP_LAYOUT,
  RBD_MY_PERF_SWITCH_OPTIONS,
  RBD_MY_PERF_PERFORMANCE_EXTRA_SWITCH_OPTIONS,
  RBD_MY_PERF_HCP_EXTRA_SWITCH_OPTIONS,
  TYVASO_DPI_WIDGET_TYPES,
} from "../Dashboard/layouts";

/**
 * Summary of Pending Referrals has no RBD data yet. It is always offered as a
 * swap-in option on the region performance tab, shown disabled ("Coming soon").
 * Flip this to true once RBD pending-referrals data is wired to enable it.
 */
export const RBD_SHOW_PENDING_REFERRALS = false;

const SALESREP_CONFIG = {
  roleKey: "SALESREP",
  showHero: true,
  heroPropsKey: "salesRepPerformanceSummary",
  // Rep has data for every widget it shows, so nothing is disabled/hidden.
  unavailableWidgetTypes: null,
  tabs: [
    {
      id: "performance",
      label: "Explore My Performance",
      layoutScope: SALESREP_MY_PERF_LAYOUT_SCOPES.performance,
      defaultLayout: SALESREP_MY_PERF_PERFORMANCE_LAYOUT,
      switchOptions: SALESREP_MY_PERF_SWITCH_OPTIONS.performance,
      extraSwitchOptions: SALESREP_MY_PERF_PERFORMANCE_EXTRA_SWITCH_OPTIONS,
      tyvasoExtraSwitchOptions: TYVASO_DPI_WIDGET_TYPES,
    },
    {
      id: "hcp",
      label: "Explore HCPs",
      layoutScope: SALESREP_MY_PERF_LAYOUT_SCOPES.hcp,
      defaultLayout: SALESREP_MY_PERF_HCP_LAYOUT,
      switchOptions: SALESREP_MY_PERF_SWITCH_OPTIONS.hcp,
      extraSwitchOptions: SALESREP_MY_PERF_HCP_EXTRA_SWITCH_OPTIONS,
    },
    {
      id: "competitor",
      label: "Explore Competitor Insights",
      layoutScope: SALESREP_MY_PERF_LAYOUT_SCOPES.competitor,
      defaultLayout: SALESREP_MY_PERF_COMPETITOR_LAYOUT,
      switchOptions: SALESREP_MY_PERF_SWITCH_OPTIONS.competitor,
    },
  ],
};

const RBD_PERFORMANCE_EXTRAS = [
  ...RBD_MY_PERF_PERFORMANCE_EXTRA_SWITCH_OPTIONS,
  // Always offered as a swap-in; disabled ("Coming soon") via the unavailable
  // set below until RBD pending-referrals data exists.
  "pendingReferrals",
];

const RBD_CONFIG = {
  roleKey: "RBD",
  showHero: true,
  heroPropsKey: "rbdPerformanceSummary",
  // Widgets without RBD data yet: hidden as grid tiles and shown disabled
  // ("Coming soon") in the swap menus. National / Regional Performance now have
  // backend data (/rbd_kpi) so they are available; remove a type from this set
  // once its RBD data is wired — that is the only change needed.
  unavailableWidgetTypes: new Set([
    "reachAndFrequency",
    "topYutrepiaWriters",
    ...(RBD_SHOW_PENDING_REFERRALS ? [] : ["pendingReferrals"]),
  ]),
  tabs: [
    {
      id: "performance",
      label: "Explore My Region's Performance",
      layoutScope: RBD_MY_PERF_LAYOUT_SCOPES.performance,
      defaultLayout: RBD_MY_PERF_PERFORMANCE_LAYOUT,
      switchOptions: RBD_MY_PERF_SWITCH_OPTIONS.performance,
      extraSwitchOptions: RBD_PERFORMANCE_EXTRAS,
      tyvasoExtraSwitchOptions: TYVASO_DPI_WIDGET_TYPES,
    },
    {
      id: "hcp",
      label: "Explore HCPs",
      layoutScope: RBD_MY_PERF_LAYOUT_SCOPES.hcp,
      defaultLayout: RBD_MY_PERF_HCP_LAYOUT,
      switchOptions: RBD_MY_PERF_SWITCH_OPTIONS.hcp,
      extraSwitchOptions: RBD_MY_PERF_HCP_EXTRA_SWITCH_OPTIONS,
    },
  ],
};

const CONFIG_BY_ROLE = {
  SALESREP: SALESREP_CONFIG,
  RBD: RBD_CONFIG,
};

/**
 * Returns the tabbed-view config for a role, or `null` for roles that keep the
 * legacy single-grid dashboard (e.g. LEADERSHIP).
 */
export function getRoleViewConfig(roleKey) {
  return CONFIG_BY_ROLE[(roleKey || "").toUpperCase()] || null;
}
